import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Search } from 'lucide-react';
import apiService from '../../services/api';
import { formatARS } from '../../utils/formatCurrency';

interface Cheque {
  id: number;
  partyType: 'supplier' | 'client';
  partyId: number;
  partyName: string;
  movementType: 'documento' | 'pago';
  amount: number;
  checkNumber: string;
  checkBank: string;
  checkIssueDate: string;
  checkDueDate: string;
  checkStatus: string;
  note: string;
}

const STATUS_LABELS: Record<string, string> = { pendiente: 'Pendiente de depositar', depositado: 'Depositado, sin acreditar', cobrado: 'Acreditado / debitado', rechazado: 'Rechazado' };
const STATUS_BADGE: Record<string, string> = {
  pendiente: 'bg-gray-100 text-gray-700 border-gray-200',
  depositado: 'bg-blue-100 text-blue-700 border-blue-200',
  cobrado: 'bg-green-100 text-green-700 border-green-200',
  rechazado: 'bg-red-100 text-red-700 border-red-200',
};

const formatDMY = (iso: string): string => {
  if (!iso) return '-';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

const daysUntil = (iso: string): number | null => {
  if (!iso) return null;
  const due = new Date(`${iso.slice(0, 10)}T00:00:00`);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / 86400000);
};

const isFutureDate = (iso: string): boolean => (daysUntil(iso) ?? 0) > 0;

const AdminCheques: React.FC = () => {
  const navigate = useNavigate();
  const [cheques, setCheques] = useState<Cheque[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<'activos' | 'todos' | 'pendiente' | 'depositado' | 'cobrado' | 'rechazado'>('todos');
  const [searchTerm, setSearchTerm] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiService.getCheques();
      setCheques(data.cheques || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudieron cargar los cheques.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const changeStatus = async (id: number, status: string) => {
    const cheque = cheques.find(c => c.id === id);
    if (status === 'cobrado' && cheque && isFutureDate(cheque.checkDueDate)) {
      setError(`El cheque vence el ${formatDMY(cheque.checkDueDate)}: todavía no puede registrarse como acreditado o debitado.`);
      return;
    }
    try {
      setError('');
      await apiService.updateAccountMovementCheckStatus(id, status);
      setCheques(current => current.map(c => c.id === id ? { ...c, checkStatus: status } : c));
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudo actualizar el estado del cheque.');
    }
  };

  const counts = useMemo(() => {
    const base = { activos: 0, todos: cheques.length, pendiente: 0, depositado: 0, cobrado: 0, rechazado: 0 };
    for (const c of cheques) {
      base[c.checkStatus as keyof typeof base] = (base[c.checkStatus as keyof typeof base] || 0) + 1;
      if (c.checkStatus === 'pendiente' || c.checkStatus === 'depositado') base.activos += 1;
    }
    return base;
  }, [cheques]);

  // "A cobrar" = cheques de clientes (te los dieron a vos, entra plata cuando se hagan efectivos).
  // "A pagar" = cheques de proveedores (los diste vos, sale plata de tu cuenta cuando se cobren).
  const activeCheques = useMemo(() => cheques.filter(c => c.checkStatus === 'pendiente' || c.checkStatus === 'depositado'), [cheques]);
  const totalACobrar = useMemo(() => activeCheques.filter(c => c.partyType === 'client').reduce((sum, c) => sum + c.amount, 0), [activeCheques]);
  const totalAPagar = useMemo(() => activeCheques.filter(c => c.partyType === 'supplier').reduce((sum, c) => sum + c.amount, 0), [activeCheques]);

  const filtered = cheques.filter(c => {
    if (statusFilter === 'activos' && !(c.checkStatus === 'pendiente' || c.checkStatus === 'depositado')) return false;
    if (statusFilter !== 'activos' && statusFilter !== 'todos' && c.checkStatus !== statusFilter) return false;
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return c.partyName.toLowerCase().includes(term) || c.checkNumber.toLowerCase().includes(term) || c.checkBank.toLowerCase().includes(term);
  });

  const tabs: Array<{ key: typeof statusFilter; label: string }> = [
    { key: 'activos', label: `Activos (${counts.activos})` },
    { key: 'todos', label: `Todos (${counts.todos})` },
    { key: 'pendiente', label: `Pendientes (${counts.pendiente})` },
    { key: 'depositado', label: `Depositados (${counts.depositado})` },
    { key: 'cobrado', label: `Cobrados (${counts.cobrado})` },
    { key: 'rechazado', label: `Rechazados (${counts.rechazado})` },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cheques</h1>
          <p className="text-gray-600">Todos los cheques cargados en cuentas corrientes de clientes y proveedores, ordenados por fecha de cobro.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm self-start"><RefreshCw className="h-4 w-4" />Actualizar</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-xl bg-white p-4 shadow-sm border border-gray-100">
          <p className="text-sm text-gray-500">Vas a cobrar (cheques de clientes, pendientes + depositados)</p>
          <p className="text-xl font-bold text-green-700">{formatARS(totalACobrar)}</p>
        </div>
        <div className="rounded-xl bg-white p-4 shadow-sm border border-gray-100">
          <p className="text-sm text-gray-500">Vas a pagar (cheques a proveedores, pendientes + depositados)</p>
          <p className="text-xl font-bold text-red-600">{formatARS(totalAPagar)}</p>
        </div>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
        Un cheque recibido no es dinero cobrado. Dejalo pendiente o depositado hasta que el banco lo acredite; recién entonces marcá “Acreditado / debitado”.
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">{error}</div>}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`rounded-full border px-3 py-1.5 text-sm font-medium ${statusFilter === tab.key ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-auto">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar por cliente, proveedor, número o banco" className="w-full sm:w-72 rounded-lg border pl-9 pr-3 py-2 text-sm" />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      <div className="divide-y md:hidden">
        {loading ? <div className="px-5 py-6 text-center text-gray-500">Cargando...</div> :
          filtered.map(c => {
            const isOpen = c.checkStatus === 'pendiente' || c.checkStatus === 'depositado';
            const remaining = daysUntil(c.checkDueDate);
            return (
              <div key={c.id} className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm text-gray-900">{formatDMY(c.checkDueDate)}</p>
                    {isOpen && remaining !== null && (
                      remaining < 0
                        ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600"><AlertTriangle className="h-3 w-3" />Vencido hace {Math.abs(remaining)} día{Math.abs(remaining) === 1 ? '' : 's'}</span>
                        : remaining <= 7
                          ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700"><AlertTriangle className="h-3 w-3" />{remaining === 0 ? 'Vence hoy' : `Vence en ${remaining} día${remaining === 1 ? '' : 's'}`}</span>
                          : <span className="text-xs text-gray-400">En {remaining} días</span>
                    )}
                  </div>
                  <span className="font-semibold">{formatARS(c.amount)}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <button onClick={() => navigate(`/admin/cuentas-corrientes/${c.partyType}/${c.partyId}`)} className="text-sm font-medium text-primary-600 hover:underline text-left">
                      {c.partyName || '(sin nombre)'}
                    </button>
                    <p className="text-xs text-gray-400">{c.partyType === 'supplier' ? 'Proveedor' : 'Cliente'}</p>
                  </div>
                  {c.movementType === 'documento' ? <span className="text-red-600 font-medium text-sm">Documento</span> : <span className="text-green-700 font-medium text-sm">Pago</span>}
                </div>
                <p className="text-sm text-gray-600">{c.checkNumber || '-'} · {c.checkBank || '-'} · Emisión: {formatDMY(c.checkIssueDate)}</p>
                <div className="flex items-center justify-between gap-2 pt-1">
                  <select value={c.checkStatus} onChange={e => changeStatus(c.id, e.target.value)} className={`rounded-full border px-2 py-1 text-xs font-semibold ${STATUS_BADGE[c.checkStatus] || STATUS_BADGE.pendiente}`}>
                    {Object.entries(STATUS_LABELS).map(([key, label]) => <option key={key} value={key} disabled={key === 'cobrado' && isFutureDate(c.checkDueDate)}>{label}</option>)}
                  </select>
                </div>
                {c.note && <p className="text-sm text-gray-500">{c.note}</p>}
              </div>
            );
          })}
        {!loading && !filtered.length && <div className="px-5 py-10 text-center text-gray-500">No hay cheques para este filtro.</div>}
      </div>
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-5 py-3">Vencimiento</th>
              <th className="px-5 py-3">Cliente / Proveedor</th>
              <th className="px-5 py-3">Tipo</th>
              <th className="px-5 py-3">Cheque</th>
              <th className="px-5 py-3">Emisión</th>
              <th className="px-5 py-3">Monto</th>
              <th className="px-5 py-3">Estado</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? <tr><td colSpan={8} className="px-5 py-6 text-center text-gray-500">Cargando...</td></tr> :
              filtered.map(c => {
                const isOpen = c.checkStatus === 'pendiente' || c.checkStatus === 'depositado';
                const remaining = daysUntil(c.checkDueDate);
                return (
                  <tr key={c.id}>
                    <td className="px-5 py-3 text-sm text-gray-700">
                      <div className="font-medium">{formatDMY(c.checkDueDate)}</div>
                      {isOpen && remaining !== null && (
                        remaining < 0
                          ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600"><AlertTriangle className="h-3 w-3" />Vencido hace {Math.abs(remaining)} día{Math.abs(remaining) === 1 ? '' : 's'}</span>
                          : remaining <= 7
                            ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700"><AlertTriangle className="h-3 w-3" />{remaining === 0 ? 'Vence hoy' : `Vence en ${remaining} día${remaining === 1 ? '' : 's'}`}</span>
                            : <span className="text-xs text-gray-400">En {remaining} días</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <button onClick={() => navigate(`/admin/cuentas-corrientes/${c.partyType}/${c.partyId}`)} className="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline text-left">
                        {c.partyName || '(sin nombre)'}
                      </button>
                      <p className="text-xs text-gray-400">{c.partyType === 'supplier' ? 'Proveedor' : 'Cliente'}</p>
                    </td>
                    <td className="px-5 py-3">{c.movementType === 'documento' ? <span className="text-red-600 font-medium text-sm">Documento</span> : <span className="text-green-700 font-medium text-sm">Pago</span>}</td>
                    <td className="px-5 py-3 text-sm text-gray-600">{c.checkNumber || '-'} · {c.checkBank || '-'}</td>
                    <td className="px-5 py-3 text-sm text-gray-500">{formatDMY(c.checkIssueDate)}</td>
                    <td className="px-5 py-3 font-semibold">{formatARS(c.amount)}</td>
                    <td className="px-5 py-3">
                      <select value={c.checkStatus} onChange={e => changeStatus(c.id, e.target.value)} className={`rounded-full border px-2 py-1 text-xs font-semibold ${STATUS_BADGE[c.checkStatus] || STATUS_BADGE.pendiente}`}>
                        {Object.entries(STATUS_LABELS).map(([key, label]) => <option key={key} value={key} disabled={key === 'cobrado' && isFutureDate(c.checkDueDate)}>{label}</option>)}
                      </select>
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-500 max-w-xs truncate">{c.note}</td>
                  </tr>
                );
              })}
            {!loading && !filtered.length && <tr><td colSpan={8} className="px-5 py-10 text-center text-gray-500">No hay cheques para este filtro.</td></tr>}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
};

export default AdminCheques;
